import { FormEvent, useState } from "react";
import { formatLong, nightsBetween } from "../../lib/dates";
import { submitQuote, type Rig } from "../../lib/bookingApi";
import { logLead } from "../../lib/analytics";

interface Props {
  rig: Rig;
  startDate: string | null;
  endDate: string | null;
  prebook?: boolean;
}

const QuoteRequestForm = ({ rig, startDate, endDate, prebook }: Props) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [destination, setDestination] = useState("");
  const [guests, setGuests] = useState("2");
  const [notes, setNotes] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [error, setError] = useState("");

  const ready = Boolean(startDate && endDate && name.trim() && email.trim());

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!startDate || !endDate) {
      setError("Select pickup and return dates on the calendar.");
      setStatus("error");
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await submitQuote({
        rig,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        startDate,
        endDate,
        destination: destination.trim() || undefined,
        guests: Number.parseInt(guests, 10) || undefined,
        notes: notes.trim() || undefined,
        ...(company ? { company } : {}),
      });
      logLead(prebook ? "Prebook" : "Quote");
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Could not send the request.");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-2xl bg-zinc-50 p-6 text-center">
        <p className="font-semibold text-lg text-alt-blk mb-2">
          {prebook ? "Pre-book received." : "Request received."}
        </p>
        <p className="text-zinc-600">
          {prebook
            ? `Thanks — we have ${formatLong(startDate!)} to ${formatLong(endDate!)} as interest, not a confirmed reservation. We’ll email when Serenity is ready to book.`
            : `Thanks! We’ll generate a quote for ${formatLong(startDate!)} to ${formatLong(endDate!)}. Keep an eye on your email for updates.`}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {startDate && endDate && (
        <p className="text-sm text-zinc-600">
          {nightsBetween(startDate, endDate)} night
          {nightsBetween(startDate, endDate) === 1 ? "" : "s"} · {formatLong(startDate)} pickup ·{" "}
          {formatLong(endDate)} return
        </p>
      )}
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Name</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
          autoComplete="name"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Email</span>
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
          autoComplete="email"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Phone</span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
          autoComplete="tel"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Where are you headed?</span>
        <input
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
          placeholder="Big Bend, the Hill Country, a family visit…"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Guests</span>
        <input
          type="number"
          min={1}
          max={6}
          value={guests}
          onChange={(e) => setGuests(e.target.value)}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-zinc-700">Notes</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-xl border border-zinc-200 px-4 py-3"
        />
      </label>
      <div className="hidden" aria-hidden="true">
        <input
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>
      {error && <p className="text-sm text-mars">{error}</p>}
      <button
        type="submit"
        disabled={!ready || status === "saving"}
        className="block w-full bg-mars hover:bg-sunset disabled:bg-zinc-300 text-white text-center py-6 rounded-2xl font-semibold text-lg transition-all"
      >
        {status === "saving" ? "Sending…" : prebook ? "Send pre-book request" : "Send quote request"}
      </button>
    </form>
  );
};

export default QuoteRequestForm;
